<script setup>

import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import StaticWidget from "@/components/common/other/StaticWidget";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import SearchStockPillGroup from "@/components/warehouse/SearchStockPillGroup";
import BusySpinner from "@/components/common/other/BusySpinner";
import StockSearchItem from "@/classes/stock/StockSearchItem";
import GridColumn from "@/classes/component-classes/GridColumn";
import Notification from "@/classes/component-classes/Notification";
import API from "@/classes/global/API";

import {inject, ref, onMounted} from 'vue';

import {useCheckFieldsForData} from "@/composables/common/useCheckFieldsForData";
import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useClearDescriptionHeaders} from "@/composables/stock/useClearDescriptionHeaders";
import {useDownloadLocalFile} from "@/composables/common/useDownloadLocalFile";

const store = useStore();
const route = useRoute();

const selectedRowId = ref();

const stockSearch = inject('stockSearch');

onMounted(() => {

  if (useCheckFieldsForData(stockSearch.value.stockSearchCriteria)) return handleStockSearch();

  prepareMetaData();
  getLatestStock();
  // getWarehouseStock();

})

async function prepareMetaData() {

  const response = await API.getStockMetaData(route, 'pickStockValidationFields' );

  if(response) stockSearch.value.metaData = response;

}

// async function getWarehouseStock() {
//
//   const response = await API.getWarehouseStock(route)
//
//   if (response) {
//
//     stockSearch.value.metaData.warehouses = response;
//
//   }
//
// }

function handleClearAllFields() {

  clearGridLineData();
  getLatestStock();

}

function handleRefresh() {

  if (stockSearch.value.isSearchData) {
    handleStockSearch();
    return;
  }

  getLatestStock();
}

async function getLatestStock() {

  clearGridLineData();

  stockSearch.value.isSearchData = false;

  const response = await API.getLatestStock(route)

  if (response) {

    stockSearch.value.searchData = response;
  }

}

function clearGridLineData() {

  stockSearch.value.lineData = [];
  stockSearch.value.tempLineCols = [];

  stockSearch.value.tempLineCols.push(new GridColumn('1', '', '', '1745', true,
      false, 50));

}

async function getRowData(row) {

  if (!stockSearch.value.isSearchData) stockSearch.value.stockSearchCriteria = new StockSearchItem();

  const response = await API.getStockLines(route, row.id, stockSearch.value.stockSearchCriteria);

  if (response.checkStockItems.length > 0) {

    stockSearch.value.tempLineCols = response.columns;
    stockSearch.value.lineData = response.checkStockItems;

    if(row.id === selectedRowId.value) {

      stockSearch.value.openDetailsDrawer = !stockSearch.value.openDetailsDrawer;

    } else {

      stockSearch.value.openDetailsDrawer = true;
    }

    selectedRowId.value = row.id;

  } else {

    clearGridLineData();
  }

}

function toggleSearchGroup(button) {

  if(button && stockSearch.value.searchGroup) return;
  if(!button && !stockSearch.value.searchGroup) return;

  stockSearch.value.searchGroup = !stockSearch.value.searchGroup;
}

async function getStockHeaders(selected) {

  stockSearch.value.lineData = [];
  clearGridLineData();
  useClearDescriptionHeaders(stockSearch.value);

  stockSearch.value.searchCriteria.skuGroup = selected.id

  const response = await API.getStockDescriptions(route, selected.id);

  if (response) {

    for (const each in response) {

      if (response[each]) {

        stockSearch.value.skuGroupSelected = true;
        stockSearch.value.searchPills[each].enabled = true;
        stockSearch.value.searchPills[each].label = response[each];

      } else {

        stockSearch.value.searchPills[each].enabled = false;

      }
    }
  }

  handleStockSearch();
}

async function clearHeaders() {

  stockSearch.value.lineData = [];

  stockSearch.value.searchCriteria.skuGroup = '';

  handleRefresh();

  clearGridLineData();
  useClearDescriptionHeaders(stockSearch.value);
  handleStockSearch();

}

async function handleStockSearch() {

  const response = await API.searchStock(stockSearch.value.searchCriteria, 'checkstock');

  stockSearch.value.searchData = response;

}

async function downloadToExcel() {

  stockSearch.value.hideExcelDownloadBusy = false;

  const response = await API.getCheckStockExcelFile();

  if(response) {

    try {

      useDownloadLocalFile(response);

      stockSearch.value.hideExcelDownloadBusy = true;

    } catch(error) {

      stockSearch.value.hideExcelDownloadBusy = true;
      store.state.banners.error = new Notification(true, 'Could Not Process The File');
    }

  }

}

</script>

<template class="cmp-stock">

  <div class="cmp-stock_wrapper">
    <StaticWidget
        title="Search & View Stock"
        height="735">
      <template #extra-content>
        <div style="margin-left: 60px; font-weight: normal; font-size: 12px">SKU Group</div>
        <div style="font-weight: normal" class="global-flex-layout">
          <MultiSearchDropDown
              :key="stockSearch.metaData.skuGroups"
              :width="280"
              :disableFilter="true"
              placeholder="Select...."
              @select="getStockHeaders"
              @clear="clearHeaders"
              :selectPrimitiveValue="false"
              :dataSource="stockSearch.metaData.skuGroups">
          </MultiSearchDropDown>
          <StaticTooltip :icon="false">
            <template #content>
              <div style="width: 340px;">
                <strong>Search</strong> by completing any field and press any search icon
                <span><i class="fa-duotone fa-square-arrow-right" style="color: #114362"></i></span> or press enter within the field.
                Any fields with data are considered when searching records, some criteria may return no results. Selecting a
                SKU Group will bring new search criteria where available. A search group toggle will appear to view other fields.
                Press Refresh to return the latest records for the current search, or reset the data to initial records.
              </div>
            </template>
            <i :class="store.state.globalHelpIcon"></i>
          </StaticTooltip>
        </div>
        <div v-if="stockSearch.skuGroupSelected" class="cmp-stock_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="1"
              :class="{'cmp-stock_search-group-selection-icon-active' : stockSearch.searchGroup}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="2"
              :class="{'cmp-stock_search-group-selection-icon-active' : !stockSearch.searchGroup}"
              style="width: 30px;">
          </Button>
        </div>
      </template>
      <SearchStockPillGroup
          @search="clearGridLineData"
          @clear="handleClearAllFields"
          url="checkstock"
          v-model="stockSearch">
      </SearchStockPillGroup>
      <StaticWidget title="Search Results" height="580">
        <template #extra-content>
          <div style="font-weight: normal; font-size: 12px; color: var(--aqua-blue-definition); margin-left: 20px;">
            Click on rows to view each line information
          </div>
          <Button
              @click="handleRefresh()"
              icon="<i class='fa-light fa-arrows-rotate'></i>"
              text="Refresh"
              type="secondary">
          </Button>
        </template>
        <template #right-side>
          <BusySpinner :hide="stockSearch.hideExcelDownloadBusy" message="Downloading Data..."></BusySpinner>
          <StaticTooltip :icon="false"  v-if="stockSearch.hideExcelDownloadBusy">
            <template #content>
              This will download a full stock list to an excel file.
              Any filters or searches are not included within
              the created file.
            </template>
            <i class="fa-solid fa-circle-info icon-info"></i>
          </StaticTooltip>
          <Button
              v-if="stockSearch.hideExcelDownloadBusy"
              @click="downloadToExcel()"
              icon='<i class="fa-solid fa-file-excel"></i>'
              text="Export All Stock Lines"
              type="success">
          </Button>
        </template>
        <div class="cmp-stock_search-results-wrapper">
          <StickyGrid
              :key="stockSearch.searchData"
              :allowGridMenu="false"
              :height="540"
              :page-size="100"
              :selectable="true"
              :highlightRows="false"
              :showDateIndicator="false"
              :show-tips="false"
              :row-selection="false"
              @selected-row="getRowData"
              v-model:currentPage="stockSearch.searchGridCurrentPage"
              v-model:currentPaginationRange="stockSearch.searchGridPageRange"
              :dataSource="stockSearch.searchData"
              :columns="stockSearch.gridColumns">
          </StickyGrid>
        </div>
      </StaticWidget>
    </StaticWidget>
  </div>

</template>

<style lang="scss">

.cmp-stock {

  &_line-results-wrapper {
    opacity: 0.8;
  }

  &_search-group-selection {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: 60px;
    font-weight: normal;
    font-size: 12px;
  }

  &_search-group-selection-icon {
    font-size: 24px;
    color: #a94e0e;
  }

  &_search-group-selection-icon-active {
    background-image: none !important;
    background-color: white;
    font-weight: bold;
    color: black !important;
    box-shadow: 1px 1px 2px 0px #484848 !important;
  }

}
</style>
